import { Bar, Line } from "@rio-cloud/rio-uikit";
import ComposedChart from "@rio-cloud/rio-uikit/ComposedChart";
import ChartTooltip from "@rio-cloud/rio-uikit/ChartTooltip";
import Legend from "@rio-cloud/rio-uikit/Legend";
import ReferenceLine from "@rio-cloud/rio-uikit/ReferenceLine";
import Card from "@rio-cloud/rio-uikit/Card";
import { formatValue } from "../../../utils/format";
import { useT } from "@hooks";

interface FinancialEvolutionItem {
  month: string;
  expenses: number;
  revenue: number;
  profit: number; // Lucro Operacional (não Saldo!)
  profitMargin: number; // (Lucro Operacional / Receita Bruta) * 100 - calculado pelo backend
}

interface FinancialEvolutionChartProps {
  data: FinancialEvolutionItem[];
}

type TraduzirMensagem = ReturnType<typeof useT>;

const formatarRotuloDaLegenda = (value: string) => (
  <span className="text-color-gray margin-left-10 margin-right-10">{value}</span>
);

const renderizarConteudoDoTooltip = (props: any, t: TraduzirMensagem) => {
  if (!props.active || !props.payload || props.payload.length === 0) return null;

  const item = props.payload[0].payload;
  const profit = item.profit;
  const revenue = item.revenue;
  const expenses = item.expenses;
  const marginValue = item.profitMargin ?? 0;
  const margin = marginValue % 1 === 0 ? marginValue.toFixed(0) : marginValue.toFixed(2);

  return (
    <div className="display-flex flex-column gap-5 padding-y-10 padding-x-15 rounded border bg-lightest text-center">
      <span>
        {t("dashboard.evolution.tooltip.revenues")} {formatValue(revenue)}
      </span>
      <span>
        {t("dashboard.evolution.tooltip.expenses")} {formatValue(expenses)}
      </span>
      <span className="text-bold">
        {t("dashboard.evolution.tooltip.profit")} {formatValue(profit)}
      </span>
      <span>
        {t("dashboard.evolution.tooltip.profitMargin")} {margin}%
      </span>
    </div>
  );
};

export default function FinancialEvolutionChart({ data }: Readonly<FinancialEvolutionChartProps>) {
  const t = useT();

  const maxRevenue = Math.max(...data.map((item) => item.revenue));
  const minExpenses = Math.min(...data.map((item) => item.expenses));

  const step = Math.ceil(maxRevenue / 300000) * 100000;

  const yAxisMax = Math.ceil(maxRevenue / step) * step;
  const yAxisMin = Math.floor(minExpenses / step) * step;

  const positiveCount = yAxisMax / step;
  const negativeCount = Math.abs(yAxisMin / step);

  const ticks = [];
  for (let i = positiveCount; i >= -negativeCount; i--) {
    ticks.push(i * step);
  }

  return (
    <Card className="padding-y-25 padding-x-15 padding-x-50-md margin-bottom-20">
      <div className="display-flex align-items-center justify-content-between gap-10 padding-bottom-20">
        <span className="text-size-16 text-bold">{t("dashboard.evolution.title")}</span>
      </div>

      <div className="overflow-x-auto">
        <div className="width-700 width-100pct-md">
          <ComposedChart
            width={1200}
            height={400}
            data={data}
            barGap={-28}
            bars={[
              <Bar
                key="revenue"
                name={t("dashboard.evolution.revenues")}
                dataKey="revenue"
                barSize={28}
                fill="#CCECCE"
                radius={[4, 4, 0, 0]}
                style={{ fill: "#CCECCE" }}
              />,
              <Bar
                key="expenses"
                name={t("dashboard.evolution.expenses")}
                dataKey="expenses"
                barSize={28}
                fill="#F4C7C0"
                radius={[4, 4, 0, 0]}
                style={{ fill: "#F4C7C0" }}
              />,
            ]}
            lines={[
              <Line
                key="profit"
                name={t("dashboard.evolution.profit")}
                type="monotone"
                dataKey="profit"
                strokeWidth={3}
                stroke="#30B4C0"
                dot={{ fill: "#fff", r: 4 }}
                activeDot={{ fill: "#30B4C0", r: 6, stroke: "none" }}
              />,
            ]}
            legend={
              <Legend
                iconSize={12}
                align="right"
                layout="horizontal"
                verticalAlign="bottom"
                payload={[
                  { value: t("dashboard.evolution.expenses"), type: "rect", color: "#F4C7C0" },
                  { value: t("dashboard.evolution.revenues"), type: "rect", color: "#CCECCE" },
                  { value: t("dashboard.evolution.profit"), type: "line", color: "#30B4C0" },
                ]}
                formatter={formatarRotuloDaLegenda}
              />
            }
            tooltip={<ChartTooltip content={(props: any) => renderizarConteudoDoTooltip(props, t)} />}
            showXAxis
            showYAxis
            showGrid={false}
            yAxisOptions={{
              domain: [yAxisMin, yAxisMax],
              axisLine: { stroke: "#A7AFBB" },
              tickFormatter: (value: number) =>
                value === 0
                  ? "0"
                  : `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              width: 100,
              ticks: ticks,
            }}
            xAxisOptions={{
              dataKey: "month",
              axisLine: false,
              tickLine: false,
            }}
            containerOptions={{ width: "100%", height: 400 }}
            referenceLines={[<ReferenceLine key="zero" y={0} stroke="#A7AFBB" strokeWidth={2} />]}
          />
        </div>
      </div>
    </Card>
  );
}
